import { STARTING_RATING } from "../../utils/elo";

export default function ReviewCompleteScreen({
    results = [],
    sessionOrigin,
    correctedCount = 0,
    totalReviewCount = 0,
    onReviewAgain,
    onNewStudy,
    onNewBuzzer,
    ratings,
    sessionStartRatings,
    ratingEligible,
}) {
    const remainingCount = Math.max(totalReviewCount - correctedCount, 0);

    const correctionRate =
        totalReviewCount > 0 ? correctedCount / totalReviewCount : 0;

    const summaryMessage = getSummaryMessage(correctedCount, totalReviewCount);
    const accentClass = getAccentClass(correctionRate);
    const categoryStats = getCategoryPerformance(results, ratings, sessionStartRatings);

    const overallRating =
        ratings?.overall ?? 1500;

    const startingOverallRating =
        sessionStartRatings?.overall ?? 1500;

    const overallRatingChange =
        overallRating - startingOverallRating;

    function formatRating(rating) {
        return Math.round(rating);
    }

    function formatRatingChange(change) {
        const rounded = Math.round(change);

        if (rounded > 0) {
            return `+${rounded}`;
        }

        if (rounded < 0) {
            return `${rounded}`;
        }

        return "–";
    }

    function getRatingChangeClass(change) {
        const rounded = Math.round(change);

        if (rounded > 0) {
            return "rating-change--positive";
        }

        if (rounded < 0) {
            return "rating-change--negative";
        }

        return "rating-change--neutral";
    }

    return (
        <div className="review-complete-screen">
            <div className="review-complete-screen__content">
                <header className="review-complete-screen__header">
                    <div className="review-complete-screen__title">Review Complete</div>
                    <div className="review-complete-screen__subtitle">
                        {totalReviewCount} review question{totalReviewCount === 1 ? "" : "s"}
                    </div>
                </header>

                <section
                    className={`review-summary-card ${accentClass}`}
                    aria-label="Review summary"
                >
                    <div className="review-summary-card__eyebrow"></div>

                    <div className="review-summary-card__score">
                        {correctedCount} / {totalReviewCount}
                    </div>

                    <div className="review-summary-card__message">{summaryMessage}</div>
                </section>

                <section
                    className="review-stats-card"
                    aria-label="Review progress details"
                >
                    <div className="review-stats-card__grid">
                        <StatCard
                            label="Accuracy"
                            value={`${Math.round(correctionRate * 100)}%`}
                            highlight
                        />
                        <StatCard
                            label="Missed"
                            value={remainingCount}
                        />
                    </div>
                </section>

                {ratingEligible && (
                    <section
                        className="review-rating-card"
                        aria-label="Overall rating"
                    >
                        <div className="review-rating-card__label">
                            Overall Rating
                        </div>

                        <div className="review-rating-card__values">
                            <span className="review-rating-card__rating">
                                {formatRating(overallRating)}
                            </span>

                            <span
                                className={`review-rating-card__change ${getRatingChangeClass(
                                    overallRatingChange
                                )}`}
                            >
                                {formatRatingChange(overallRatingChange)}
                            </span>
                        </div>
                    </section>
                )}

                <section
                    className="review-insights-card"
                    aria-label="Remaining weaknesses"
                >
                    <div className="review-insights-card__title">
                        Performance By Category
                    </div>

                    {remainingCount === 0 && (
                        <div className="review-insights-card__empty">
                            You answered every review question correctly.
                        </div>
                    )}

                    <div className="review-complete__category-list">
                        {categoryStats.map((stat) => {
                            return (
                                <div
                                    className={`review-complete__category-row ${!ratingEligible
                                        ? "review-complete__category-row--compact"
                                        : ""
                                        }`}
                                    key={stat.category}
                                >
                                    <div className="review-complete__category-name">
                                        {stat.category}
                                    </div>

                                    <div className="review-complete__category-details">
                                        <div className="review-complete__category-performance">
                                            <span className="review-complete__category-score">
                                                {stat.correct} / {stat.total}
                                            </span>

                                            <span
                                                className={`review-complete__category-accuracy ${getAccuracyClass(
                                                    stat.accuracy
                                                )}`}
                                            >
                                                {stat.accuracy}%
                                            </span>
                                        </div>

                                        {ratingEligible && (
                                            <div className="review-complete__category-rating">
                                                <span className="review-complete__category-rating-value">
                                                    {formatRating(stat.rating)}
                                                </span>

                                                <span
                                                    className={`review-complete__category-rating-change ${getRatingChangeClass(
                                                        stat.ratingChange
                                                    )}`}
                                                >
                                                    {formatRatingChange(stat.ratingChange)}
                                                </span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>

                <section className="review-actions" aria-label="Review actions">
                    {remainingCount > 0 && (
                        <button
                            type="button"
                            className="review-actions__primary"
                            onClick={onReviewAgain}
                        >
                            Review Missed
                        </button>
                    )}

                    {sessionOrigin === "buzzer" && (
                        <button
                            type="button"
                            className={
                                remainingCount > 0
                                    ? "review-actions__secondary-primary"
                                    : "review-actions__primary"
                            }
                            onClick={onNewBuzzer}
                        >
                            New Buzzer Session
                        </button>
                    )}

                    {sessionOrigin === "study" && (
                        <button
                            type="button"
                            className={
                                remainingCount > 0
                                    ? "review-actions__secondary-primary"
                                    : "review-actions__primary"
                            }
                            onClick={onNewStudy}
                        >
                            New Study Session
                        </button>
                    )}

                </section>
            </div>
        </div>
    );
}

function StatCard({ label, value, highlight = false }) {
    return (
        <div
            className={`review-stats-card__item ${highlight ? "review-stats-card__item--highlight" : ""
                }`}
        >
            <div className="review-stats-card__label">{label}</div>
            <div className="review-stats-card__value">{value}</div>
        </div>
    );
}

function getSummaryMessage(correctedCount, totalReviewCount) {
    if (totalReviewCount === 0) {
        return "There were no missed questions to review.";
    }

    const rate = correctedCount / totalReviewCount;

    if (rate === 1) {
        return "Excellent work — you’re learning quickly.";
    }

    if (rate >= 0.8) {
        return "Strong session — just a bit more repetition will lock it in.";
    }

    if (rate >= 0.5) {
        return "Solid effort — focus on a few weak areas to improve.";
    }

    if (rate > 0) {
        return "Keep practicing — understanding will come with time.";
    }

    return "These questions still need more practice.";
}

function getAccentClass(rate) {
    if (rate === 1 || rate >= 0.8) {
        return "review-summary-card--success";
    }

    if (rate >= 0.5) {
        return "review-summary-card--warning";
    }

    return "review-summary-card--danger";
}

function getCategoryPerformance(
    results,
    ratings,
    sessionStartRatings
) {
    const stats = new Map();

    for (const result of results) {
        const category =
            result?.category?.trim() || "Uncategorized";

        const categoryId = String(result.categoryId);

        const isCorrect =
            result.outcome === "correct_buzz";

        if (!stats.has(category)) {
            stats.set(category, {
                category,
                categoryId,
                correct: 0,
                total: 0,
            });
        }

        const entry = stats.get(category);

        entry.total += 1;

        if (isCorrect) {
            entry.correct += 1;
        }
    }

    return Array.from(stats.values())
        .map((item) => {
            const currentRating =
                ratings?.categories?.[item.categoryId]?.rating ??
                null;

            const startingRating =
                sessionStartRatings
                    ?.categories?.[item.categoryId]?.rating ??
                STARTING_RATING;

            const ratingChange =
                currentRating != null
                    ? currentRating - startingRating
                    : 0;

            return {
                ...item,

                accuracy: Math.round(
                    (item.correct / item.total) * 100
                ),

                rating: currentRating,

                ratingChange,
            };
        })
        .sort((a, b) => {
            const aChange = Math.round(a.ratingChange);
            const bChange = Math.round(b.ratingChange);

            // 1. Losses come first.
            if (aChange < 0 && bChange >= 0) {
                return -1;
            }

            if (aChange >= 0 && bChange < 0) {
                return 1;
            }

            // 2. Among losses, biggest loss first.
            // Example: -40 before -10.
            if (aChange < 0 && bChange < 0) {
                return aChange - bChange;
            }

            // 3. Gains come next, biggest gain first.
            // Example: +80 before +20.
            if (aChange > 0 && bChange > 0) {
                return bChange - aChange;
            }

            // 4. Zero changes go after non-zero gains.
            if (aChange === 0 && bChange > 0) {
                return 1;
            }

            if (aChange > 0 && bChange === 0) {
                return -1;
            }

            // 5. Tie-break alphabetically.
            return a.category.localeCompare(b.category);
        });
}

function getAccuracyClass(accuracy) {
    if (accuracy >= 80) return "accuracy--high";
    if (accuracy >= 50) return "accuracy--medium";
    return "accuracy--low";
}